import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { fr as frLocale, es as esLocale, enUS } from 'date-fns/locale';
import { ArrowUp, ArrowDown, Minus, X, GitCompare } from 'lucide-react';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid } from 'recharts';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '../ui/select';
import { TimeRangePicker } from './TimeRangePicker';

const LOCALES = { fr: frLocale, es: esLocale, en: enUS };
const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ef4444'];

const ENTITY_SOURCES = {
    business: { url: '/organizations', id: o => o.id, label: o => o.name },
    location: { url: '/locations', id: l => l.id, label: l => l.name },
    card: { url: '/cards', id: c => c.card_id, label: c => c.employee_name ? `${c.card_id} (${c.employee_name})` : c.card_id },
    employee: { url: '/employees', id: e => e.id, label: e => e.name },
};

const ChangeBadge = ({ abs, pct, t }) => {
    if (abs === 0) return <span className="inline-flex items-center gap-1 text-muted-foreground text-sm"><Minus className="w-3.5 h-3.5" />{t('analyticsCompare.noChange')}</span>;
    const up = abs > 0;
    const Icon = up ? ArrowUp : ArrowDown;
    return (
        <span className={`inline-flex items-center gap-1 text-sm font-semibold ${up ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>
            <Icon className="w-3.5 h-3.5" />
            {abs > 0 ? '+' : ''}{abs}{pct !== null && pct !== undefined ? ` (${pct > 0 ? '+' : ''}${pct}%)` : ''}
        </span>
    );
};

export const CompareMode = ({ timeRange, isSuperAdmin, onClose }) => {
    const { t, i18n } = useTranslation();
    const locale = LOCALES[(i18n.language || 'en').slice(0, 2)] || enUS;
    const [entityType, setEntityType] = useState(null);
    const [options, setOptions] = useState([]);
    const [selected, setSelected] = useState([]);
    const [search, setSearch] = useState('');
    const [twoPeriods, setTwoPeriods] = useState(false);
    const [periodA, setPeriodA] = useState({ start: timeRange.start, end: timeRange.end, labelKey: timeRange.labelKey });
    const [periodB, setPeriodB] = useState(null);
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);
    const [metric, setMetric] = useState('totalTaps');

    const types = ['location', 'card', 'employee'];
    if (isSuperAdmin) types.unshift('business');

    useEffect(() => {
        if (!entityType) return;
        const src = ENTITY_SOURCES[entityType];
        api.get(src.url)
            .then(res => setOptions(res.data.map(o => ({ id: src.id(o), label: src.label(o) })).filter(o => o.id)))
            .catch(() => toast.error(t('analyticsCompare.loadFailed')));
        setSelected([]);
        setData(null);
    }, [entityType]);

    useEffect(() => {
        if (twoPeriods && !periodB) {
            const len = periodA.end - periodA.start;
            setPeriodB({ start: new Date(periodA.start - len), end: new Date(periodA.start), labelKey: null });
        }
    }, [twoPeriods]);

    const filtered = useMemo(() =>
        options.filter(o => o.label.toLowerCase().includes(search.toLowerCase()) && !selected.find(s => s.id === o.id)).slice(0, 8),
        [options, search, selected]);

    const run = async () => {
        setLoading(true);
        try {
            const params = {
                entity_type: entityType === 'business' ? 'organization' : entityType,
                ids: selected.map(s => s.id).join(','),
                a_start: periodA.start.toISOString().slice(0, 19),
                a_end: periodA.end.toISOString().slice(0, 19),
            };
            if (twoPeriods && periodB) {
                params.b_start = periodB.start.toISOString().slice(0, 19);
                params.b_end = periodB.end.toISOString().slice(0, 19);
            }
            const res = await api.get('/analytics/compare', { params });
            setData(res.data);
        } catch {
            toast.error(t('analyticsCompare.loadFailed'));
        } finally {
            setLoading(false);
        }
    };

    const fmtP = (p) => {
        const dfmt = (i18n.language || 'en').startsWith('en') ? 'MMM d' : 'd MMM';
        return `${format(p.start, dfmt, { locale })} – ${format(p.end, dfmt, { locale })}`;
    };

    // Chronological presentation: earlier period first, change = later − earlier
    const bEarlier = twoPeriods && periodB && periodB.start < periodA.start;
    const firstP = bEarlier ? periodB : periodA;
    const secondP = bEarlier ? periodA : periodB;
    const rowVals = (r) => {
        if (!r.b) return { first: r.a.total_taps, second: null, abs: null, pct: null };
        const earlier = bEarlier ? r.b.total_taps : r.a.total_taps;
        const later = bEarlier ? r.a.total_taps : r.b.total_taps;
        const abs = later - earlier;
        const pct = earlier ? Math.round((abs / earlier) * 1000) / 10 : null;
        return { first: earlier, second: later, abs, pct };
    };

    const chartData = useMemo(() => {
        if (!data) return [];
        if (metric === 'tapsOverTime') {
            const buckets = {};
            data.results.forEach(r => r.a.series.forEach(s => {
                buckets[s.bucket] = buckets[s.bucket] || { bucket: s.bucket };
                buckets[s.bucket][r.name] = s.taps;
            }));
            return Object.values(buckets).sort((a, b) => a.bucket.localeCompare(b.bucket));
        }
        const key = { totalTaps: 'total_taps', avgDaily: 'avg_daily_taps', peakHour: 'peak_hour_taps' }[metric];
        return data.results.map(r => ({
            name: r.name.length > 18 ? r.name.slice(0, 18) + '…' : r.name,
            [t('analyticsCompare.periodA')]: r.a[key] ?? 0,
            ...(r.b ? { [t('analyticsCompare.periodB')]: r.b[key] ?? 0 } : {}),
        }));
    }, [data, metric, t]);

    return (
        <Card data-testid="compare-mode-panel" className="border-primary/40">
            <CardContent className="p-4 space-y-4">
                <div className="flex items-center justify-between">
                    <p className="font-semibold flex items-center gap-2"><GitCompare className="w-4 h-4 text-primary" />{t('analyticsCompare.title')}</p>
                    <Button variant="ghost" size="sm" onClick={onClose} data-testid="compare-exit-btn"><X className="w-4 h-4 mr-1" />{t('analyticsCompare.exit')}</Button>
                </div>

                {/* Step 1 — What */}
                <div>
                    <p className="text-sm font-medium mb-2">1. {t('analyticsCompare.what')}</p>
                    <div className="flex flex-wrap gap-2">
                        {types.map(ty => (
                            <Button key={ty} variant={entityType === ty ? 'default' : 'outline'} size="sm"
                                onClick={() => setEntityType(ty)} data-testid={`compare-type-${ty}`}>
                                {t(`analyticsCompare.types.${ty}`)}
                            </Button>
                        ))}
                    </div>
                </div>

                {/* Step 2 — Who */}
                {entityType && (
                    <div>
                        <p className="text-sm font-medium mb-2">2. {t('analyticsCompare.who')} <span className="text-muted-foreground font-normal">({t('analyticsCompare.selectedCount', { n: selected.length })})</span></p>
                        {selected.length > 0 && (
                            <div className="flex flex-wrap gap-1.5 mb-2">
                                {selected.map(s => (
                                    <Badge key={s.id} variant="secondary" className="pl-2 pr-1 py-1 gap-1" data-testid={`compare-chip-${s.id}`}>
                                        {s.label}
                                        <button onClick={() => setSelected(sel => sel.filter(x => x.id !== s.id))} aria-label={t('analyticsCompare.remove')} className="hover:text-destructive"><X className="w-3 h-3" /></button>
                                    </Badge>
                                ))}
                            </div>
                        )}
                        {selected.length < 5 && (
                            <div className="relative">
                                <Input value={search} onChange={e => setSearch(e.target.value)} placeholder={t('analyticsCompare.searchPlaceholder')} className="h-9" data-testid="compare-search-input" />
                                {search && filtered.length > 0 && (
                                    <div className="absolute z-20 mt-1 w-full rounded-md border bg-popover shadow-md max-h-56 overflow-auto">
                                        {filtered.map(o => (
                                            <button key={o.id} className="w-full text-left px-3 py-2 text-sm hover:bg-muted"
                                                onClick={() => { setSelected(s => [...s, o]); setSearch(''); }}
                                                data-testid={`compare-option-${o.id}`}>
                                                {o.label}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {/* Step 3 — When */}
                {selected.length >= 2 && (
                    <div>
                        <p className="text-sm font-medium mb-2">3. {t('analyticsCompare.when')}</p>
                        <div className="flex flex-wrap gap-2 mb-2">
                            <Button variant={!twoPeriods ? 'default' : 'outline'} size="sm" onClick={() => setTwoPeriods(false)} data-testid="compare-one-period">{t('analyticsCompare.onePeriod')}</Button>
                            <Button variant={twoPeriods ? 'default' : 'outline'} size="sm" onClick={() => setTwoPeriods(true)} data-testid="compare-two-periods">{t('analyticsCompare.twoPeriodsBtn')}</Button>
                        </div>
                        <div className="flex flex-col sm:flex-row gap-2">
                            <div className="flex items-center gap-2">
                                <span className="text-xs text-muted-foreground w-16">{t('analyticsCompare.periodA')}</span>
                                <TimeRangePicker value={periodA} onChange={setPeriodA} />
                            </div>
                            {twoPeriods && periodB && (
                                <div className="flex items-center gap-2">
                                    <span className="text-xs text-muted-foreground w-16">{t('analyticsCompare.periodB')}</span>
                                    <TimeRangePicker value={periodB} onChange={setPeriodB} />
                                </div>
                            )}
                        </div>
                        <Button className="mt-3 w-full sm:w-auto" onClick={run} disabled={loading} data-testid="compare-run-btn">
                            {loading ? t('analyticsCompare.loading') : t('analyticsCompare.run')}
                        </Button>
                    </div>
                )}

                {/* Results */}
                {data && (
                    <div className="space-y-4 pt-2 border-t" data-testid="compare-results">
                        {/* Desktop table */}
                        <div className="hidden sm:block overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="text-left text-muted-foreground border-b">
                                        <th className="py-2 pr-3">{t(`analyticsCompare.types.${entityType}`)}</th>
                                        <th className="py-2 pr-3">{fmtP(firstP)}</th>
                                        {twoPeriods && secondP && <th className="py-2 pr-3">{fmtP(secondP)}</th>}
                                        {twoPeriods && <th className="py-2">{t('analyticsCompare.change')}</th>}
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.results.map(r => {
                                        const v = rowVals(r);
                                        return (
                                        <tr key={r.id} className="border-b last:border-0" data-testid={`compare-row-${r.id}`}>
                                            <td className="py-2 pr-3 font-medium">{r.name}</td>
                                            <td className="py-2 pr-3">{v.first}</td>
                                            {v.second !== null && <td className="py-2 pr-3">{v.second}</td>}
                                            {v.abs !== null && <td className="py-2"><ChangeBadge abs={v.abs} pct={v.pct} t={t} /></td>}
                                        </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                        {/* Mobile cards */}
                        <div className="sm:hidden space-y-2">
                            {data.results.map(r => {
                                const v = rowVals(r);
                                return (
                                <div key={r.id} className="rounded-lg border p-3">
                                    <p className="font-medium text-sm mb-1">{r.name}</p>
                                    <div className="flex items-center justify-between text-sm">
                                        <span className="text-muted-foreground">{fmtP(firstP)}: <b className="text-foreground">{v.first}</b></span>
                                        {v.second !== null && <span className="text-muted-foreground">{fmtP(secondP)}: <b className="text-foreground">{v.second}</b></span>}
                                    </div>
                                    {v.abs !== null && <div className="mt-1"><ChangeBadge abs={v.abs} pct={v.pct} t={t} /></div>}
                                </div>
                                );
                            })}
                        </div>

                        {/* Chart + metric selector */}
                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <p className="text-sm font-medium">{t(`analyticsCompare.metrics.${metric}`)}</p>
                                <Select value={metric} onValueChange={setMetric}>
                                    <SelectTrigger className="w-44 h-8 text-sm" data-testid="compare-metric-select"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {['totalTaps', 'avgDaily', 'peakHour', 'tapsOverTime'].map(m => (
                                            <SelectItem key={m} value={m}>{t(`analyticsCompare.metrics.${m}`)}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="h-64" data-testid="compare-chart">
                                <ResponsiveContainer width="100%" height="100%">
                                    {metric === 'tapsOverTime' ? (
                                        <LineChart data={chartData}>
                                            <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                                            <XAxis dataKey="bucket" tick={{ fontSize: 10 }} />
                                            <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
                                            <Tooltip /><Legend />
                                            {data.results.map((r, i) => (
                                                <Line key={r.id} type="monotone" dataKey={r.name} stroke={COLORS[i % 5]} dot={false} strokeWidth={2} />
                                            ))}
                                        </LineChart>
                                    ) : (
                                        <BarChart data={chartData}>
                                            <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                                            <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                                            <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
                                            <Tooltip /><Legend />
                                            <Bar dataKey={t('analyticsCompare.periodA')} fill={COLORS[0]} radius={[4, 4, 0, 0]} />
                                            {twoPeriods && <Bar dataKey={t('analyticsCompare.periodB')} fill={COLORS[1]} radius={[4, 4, 0, 0]} />}
                                        </BarChart>
                                    )}
                                </ResponsiveContainer>
                            </div>
                        </div>
                    </div>
                )}
                {data && data.results.length === 0 && <p className="text-sm text-muted-foreground">{t('analyticsCompare.empty')}</p>}
            </CardContent>
        </Card>
    );
};
